import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Loader2,
  Mail,
  Radio,
  RefreshCw,
  Search,
  SendHorizonal,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import SubscriberHoverCard from '../Common/SubscriberHoverCard';
import SubscriberAvatar from '../Common/SubscriberAvatar';
import SendScheduledEmailNowModal from '../Subscribers/SendScheduledEmailNowModal';
import type { ScheduledEmailRow, ScheduledEmailStatus } from '../Subscribers/types';
import ScheduledEmailPreviewModal from './ScheduledEmailPreviewModal';
import type { AutomationStepRow } from './types';

interface AutomationScheduledSendsTabProps {
  automationId: string;
  automationName: string;
  steps?: AutomationStepRow[];
}

type StatusFilter = 'all' | ScheduledEmailStatus;

const STATUS_TABS: readonly { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'sending', label: 'Sending' },
  { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
  { key: 'canceled', label: 'Canceled' },
] as const;

interface AutomationSendRow extends ScheduledEmailRow {
  subscribers: {
    email: string | null;
    name: string | null;
    role?: string | null;
    email_status?: string | null;
    funnel_status?: string | null;
    source?: string | null;
    profile_score?: number | null;
    profile_score_tier?: string | null;
    custom_data?: unknown;
  } | null;
  automation_steps: {
    name: string | null;
    position: number;
    subject: string | null;
  } | null;
}

interface Counts {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  canceled: number;
  total: number;
}

const PAGE_SIZE = 50;

const STATUS_PILL: Record<string, { label: string; cls: string; Icon: typeof Clock }> = {
  pending: {
    label: 'Pending',
    cls: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200',
    Icon: Clock,
  },
  sending: {
    label: 'Sending',
    cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
    Icon: Loader2,
  },
  sent: {
    label: 'Sent',
    cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
    Icon: CheckCircle2,
  },
  failed: {
    label: 'Failed',
    cls: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200',
    Icon: XCircle,
  },
  canceled: {
    label: 'Canceled',
    cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    Icon: Ban,
  },
};

function StatusPill({ status }: { status: string }) {
  const def =
    STATUS_PILL[status] ?? {
      label: status,
      cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
      Icon: Clock,
    };
  const Icon = def.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${def.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
      {def.label}
    </span>
  );
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

const RELATIVE_UNITS: Array<{ limit: number; div: number; one: string; many: string }> = [
  { limit: 60, div: 1, one: 'sec', many: 'sec' },
  { limit: 60 * 60, div: 60, one: 'min', many: 'min' },
  { limit: 60 * 60 * 24, div: 60 * 60, one: 'hour', many: 'hours' },
  { limit: 60 * 60 * 24 * 30, div: 60 * 60 * 24, one: 'day', many: 'days' },
  { limit: 60 * 60 * 24 * 365, div: 60 * 60 * 24 * 30, one: 'month', many: 'months' },
  { limit: Infinity, div: 60 * 60 * 24 * 365, one: 'year', many: 'years' },
];

function formatTimeAgo(targetIso: string | null, nowMs = Date.now()): string | null {
  if (!targetIso) return null;
  const targetMs = new Date(targetIso).getTime();
  if (Number.isNaN(targetMs)) return null;
  const diffSec = Math.round((nowMs - targetMs) / 1000);
  if (diffSec < 0) {
    const ahead = Math.abs(diffSec);
    if (ahead < 60) return 'in a moment';
    for (const u of RELATIVE_UNITS) {
      if (ahead < u.limit) {
        const n = Math.max(1, Math.round(ahead / u.div));
        return `in ${n} ${n === 1 ? u.one : u.many}`;
      }
    }
    return 'upcoming';
  }
  if (diffSec < 30) return 'just now';
  for (const u of RELATIVE_UNITS) {
    if (diffSec < u.limit) {
      const n = Math.max(1, Math.round(diffSec / u.div));
      return `${n} ${n === 1 ? u.one : u.many} ago`;
    }
  }
  return 'a while ago';
}

function ScheduledWhen({ value }: { value: string | null }) {
  const absolute = formatTime(value);
  const relative = formatTimeAgo(value);
  return (
    <div>
      <div>{absolute}</div>
      {relative ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">{relative}</div>
      ) : null}
    </div>
  );
}

function stepLabel(row: AutomationSendRow): string {
  const s = row.automation_steps;
  if (s?.name?.trim()) return s.name.trim();
  if (s?.subject?.trim()) return s.subject.trim();
  if (s?.position != null) return `Step ${s.position}`;
  return '—';
}

function automationStepOptionLabel(step: AutomationStepRow): string {
  const detail = step.name?.trim() || step.subject?.trim() || 'Untitled';
  const inactive = step.active === false ? ' (inactive)' : '';
  return `Step ${step.position} · ${detail}${inactive}`;
}

function statusTabCount(counts: Counts, key: StatusFilter): number {
  if (key === 'all') return counts.total;
  return counts[key] ?? 0;
}

function escapeIlike(value: string): string {
  return value.replace(/[%_\\]/g, '\\$&');
}

export default function AutomationScheduledSendsTab({
  automationId,
  automationName,
  steps = [],
}: AutomationScheduledSendsTabProps) {
  const [counts, setCounts] = useState<Counts>({
    pending: 0,
    sending: 0,
    sent: 0,
    failed: 0,
    canceled: 0,
    total: 0,
  });
  const [rows, setRows] = useState<AutomationSendRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [stepFilter, setStepFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [scheduledSortDir, setScheduledSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [sendNowRow, setSendNowRow] = useState<ScheduledEmailRow | null>(null);
  const [previewRow, setPreviewRow] = useState<AutomationSendRow | null>(null);
  const [listTotal, setListTotal] = useState(0);
  const [sendsRealtime, setSendsRealtime] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const statusKeys = ['pending', 'sending', 'sent', 'failed', 'canceled'] as const;
      const countForAutomation = (status?: (typeof statusKeys)[number]) => {
        let q = supabase
          .from('scheduled_emails')
          .select('id, automation_enrollments!inner(automation_id)', { count: 'exact', head: true })
          .eq('automation_enrollments.automation_id', automationId);
        if (status) q = q.eq('status', status);
        return q;
      };

      const [totalRes, ...statusResults] = await Promise.all([
        countForAutomation(),
        ...statusKeys.map((status) => countForAutomation(status)),
      ]);

      if (totalRes.error) throw totalRes.error;
      for (const res of statusResults) {
        if (res.error) throw res.error;
      }

      setCounts({
        total: totalRes.count ?? 0,
        pending: statusResults[0].count ?? 0,
        sending: statusResults[1].count ?? 0,
        sent: statusResults[2].count ?? 0,
        failed: statusResults[3].count ?? 0,
        canceled: statusResults[4].count ?? 0,
      });
    } catch (e) {
      console.warn('loadCounts:', e instanceof Error ? e.message : e);
    }
  }, [automationId]);

  const loadRows = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    const searchTrim = searchQuery.trim();
    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const select = `
          id, subscriber_id, name, subject, body_html, postmark_template_alias, content_mode,
          scheduled_at, status, sent_at, error, provider, attempts,
          automation_enrollment_id, automation_step_id, created_at,
          subscribers(email, name, role, email_status, funnel_status, source, profile_score, profile_score_tier, custom_data),
          automation_steps(name, position, subject),
          automation_enrollments!inner(automation_id)
        `;

      let matchingIds: string[] | null = null;
      if (searchTrim) {
        const pattern = `%${escapeIlike(searchTrim)}%`;

        let subjectQuery = supabase
          .from('scheduled_emails')
          .select('id, automation_enrollments!inner(automation_id)')
          .eq('automation_enrollments.automation_id', automationId)
          .ilike('subject', pattern);

        let emailQuery = supabase
          .from('scheduled_emails')
          .select('id, subscribers!inner(email), automation_enrollments!inner(automation_id)')
          .eq('automation_enrollments.automation_id', automationId)
          .ilike('subscribers.email', pattern);

        if (statusFilter !== 'all') {
          subjectQuery = subjectQuery.eq('status', statusFilter);
          emailQuery = emailQuery.eq('status', statusFilter);
        }
        if (stepFilter !== 'all') {
          subjectQuery = subjectQuery.eq('automation_step_id', stepFilter);
          emailQuery = emailQuery.eq('automation_step_id', stepFilter);
        }

        const [subjectRes, emailRes] = await Promise.all([subjectQuery, emailQuery]);
        if (subjectRes.error) throw subjectRes.error;
        if (emailRes.error) throw emailRes.error;

        matchingIds = Array.from(
          new Set([
            ...((subjectRes.data ?? []) as { id: string }[]).map((row) => row.id),
            ...((emailRes.data ?? []) as { id: string }[]).map((row) => row.id),
          ]),
        );

        if (matchingIds.length === 0) {
          setRows([]);
          setListTotal(0);
          return;
        }
      }

      let q = supabase
        .from('scheduled_emails')
        .select(select, { count: 'exact' })
        .eq('automation_enrollments.automation_id', automationId)
        .order('scheduled_at', { ascending: scheduledSortDir === 'asc' })
        .order('created_at', { ascending: scheduledSortDir === 'asc' });

      if (statusFilter !== 'all') {
        q = q.eq('status', statusFilter);
      }
      if (stepFilter !== 'all') {
        q = q.eq('automation_step_id', stepFilter);
      }
      if (matchingIds) {
        q = q.in('id', matchingIds);
      }

      const { data, error: err, count } = await q.range(from, to);
      if (err) throw err;
      setRows((data ?? []) as unknown as AutomationSendRow[]);
      if (typeof count === 'number') {
        setListTotal(count);
      }
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Load failed');
        setRows([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [automationId, statusFilter, stepFilter, searchQuery, page, scheduledSortDir]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const refreshSilentRef = useRef(() => {
    void loadCounts();
    void loadRows({ silent: true });
  });
  useEffect(() => {
    refreshSilentRef.current = () => {
      void loadCounts();
      void loadRows({ silent: true });
    };
  }, [loadCounts, loadRows]);

  // Live updates while this tab is mounted. Realtime can't filter by joined
  // automation_id, so we listen to enrollment changes for this automation and
  // poll scheduled_emails as a backstop (status flips pending → sent). Broad
  // scheduled_emails events are debounced to avoid refetch storms from cron.
  useEffect(() => {
    setSendsRealtime(false);
    let debounceTimer: number | null = null;
    const scheduleRefresh = () => {
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        refreshSilentRef.current();
      }, 400);
    };

    const channel = supabase
      .channel(`automation_sends:automation:${automationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_enrollments',
          filter: `automation_id=eq.${automationId}`,
        },
        () => {
          scheduleRefresh();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scheduled_emails',
        },
        () => {
          scheduleRefresh();
        },
      )
      .subscribe((status) => {
        setSendsRealtime(status === 'SUBSCRIBED');
      });

    return () => {
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      void supabase.removeChannel(channel);
    };
  }, [automationId]);

  useEffect(() => {
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshSilentRef.current();
    };
    const intervalMs = sendsRealtime ? 60_000 : 15_000;
    const handle = window.setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sendsRealtime]);

  const handleStatusFilter = (key: StatusFilter) => {
    setPage(0);
    setStatusFilter(key);
  };

  const handleSearchChange = (value: string) => {
    setPage(0);
    setSearchQuery(value);
  };

  const handleStepFilterChange = (value: string) => {
    setPage(0);
    setStepFilter(value);
  };

  const setScheduledSort = (dir: 'asc' | 'desc') => {
    setPage(0);
    setScheduledSortDir(dir);
  };

  const toggleScheduledSort = () => {
    setScheduledSort(scheduledSortDir === 'desc' ? 'asc' : 'desc');
  };

  const refreshAll = () => {
    void loadCounts();
    void loadRows();
  };

  const modalRow: ScheduledEmailRow | null = sendNowRow;

  return (
    <div className="space-y-3 sm:space-y-5 min-w-0">
      <SendScheduledEmailNowModal
        isOpen={modalRow != null}
        row={modalRow}
        automationName={automationName}
        onClose={() => setSendNowRow(null)}
        onSuccess={() => {
          toast.success('Send queued — refreshing…');
          refreshAll();
        }}
      />

      <ScheduledEmailPreviewModal
        row={previewRow}
        subscriberLabel={
          previewRow?.subscribers?.email?.trim() ||
          previewRow?.subscribers?.name?.trim() ||
          null
        }
        stepLabel={previewRow ? stepLabel(previewRow) : null}
        onClose={() => setPreviewRow(null)}
      />

      <div className="hidden sm:flex items-start justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          All scheduled emails created by enrollments in this automation (every step per subscriber).
          Use Preview to see the send-faithful HTML before it goes out.
        </p>
        {sendsRealtime ? (
          <span
            title="Live updates connected"
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 shrink-0"
          >
            <Radio className="w-3 h-3" />
            Live
          </span>
        ) : null}
      </div>

      <div className="w-full min-w-0 overflow-x-auto">
        <div
          className="inline-flex items-center gap-1 min-w-max bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700"
          role="tablist"
          aria-label="Filter scheduled sends by status"
        >
          {STATUS_TABS.map((tab) => {
            const count = statusTabCount(counts, tab.key);
            const selected = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => handleStatusFilter(tab.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selected
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tab.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums ${
                    selected
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                  }`}
                >
                  {count.toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
        {steps.length > 0 ? (
          <select
            value={stepFilter}
            onChange={(e) => handleStepFilterChange(e.target.value)}
            className="min-w-0 flex-[0.85] sm:flex-none sm:max-w-[11rem] px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            aria-label="Filter by automation step"
          >
            <option value="all">All steps</option>
            {steps.map((step) => (
              <option key={step.id} value={step.id}>
                {automationStepOptionLabel(step)}
              </option>
            ))}
          </select>
        ) : null}
        <select
          value={scheduledSortDir}
          onChange={(e) => setScheduledSort(e.target.value === 'asc' ? 'asc' : 'desc')}
          className="min-w-0 flex-[0.9] sm:flex-none sm:w-auto px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shrink-0"
          aria-label="Sort scheduled emails"
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 sm:pl-9 pr-2 sm:pr-3 py-1.5 sm:py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            aria-label="Search subscriber email or subject"
          />
        </div>
        <button
          type="button"
          onClick={refreshAll}
          title="Refresh"
          aria-label="Refresh"
          className="inline-flex items-center justify-center p-1.5 sm:p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-none sm:rounded-xl border-0 sm:border sm:border-gray-200 sm:dark:border-gray-700 overflow-hidden bg-transparent sm:bg-white sm:dark:bg-gray-800 min-w-0">
        {loading && rows.length === 0 ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 dark:text-red-300">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
            <Mail className="w-6 h-6 mx-auto mb-2 opacity-50" />
            {counts.total === 0
              ? 'No scheduled sends yet. Enroll a subscriber to materialize step emails.'
              : 'No rows match the current filters.'}
          </div>
        ) : (
          <div className="overflow-x-auto min-w-0">
            <table className="min-w-full w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Subscriber</th>
                  <th className="hidden sm:table-cell text-left px-4 py-2 font-medium">Step</th>
                  <th className="hidden sm:table-cell text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">
                    <button
                      type="button"
                      onClick={toggleScheduledSort}
                      className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-800 dark:hover:text-gray-200"
                      title={
                        scheduledSortDir === 'desc'
                          ? 'Sorted newest first — click for oldest first'
                          : 'Sorted oldest first — click for newest first'
                      }
                      aria-label={`Sort by scheduled date, currently ${
                        scheduledSortDir === 'desc' ? 'newest first' : 'oldest first'
                      }`}
                    >
                      Scheduled
                      {scheduledSortDir === 'asc' ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-2 font-medium">Sent</th>
                  <th className="hidden sm:table-cell text-right px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="text-gray-800 dark:text-gray-200">
                    <td className="px-4 py-2 sm:py-3 min-w-0">
                      {r.subscriber_id ? (
                        <div className="flex items-start gap-3 min-w-0">
                          <SubscriberAvatar
                            customData={r.subscribers?.custom_data}
                            name={r.subscribers?.name}
                            email={r.subscribers?.email}
                          />
                          <SubscriberHoverCard
                            subscriberId={r.subscriber_id}
                            seed={{
                              email: r.subscribers?.email,
                              name: r.subscribers?.name,
                              role: r.subscribers?.role,
                              email_status: r.subscribers?.email_status,
                              funnel_status: r.subscribers?.funnel_status,
                              source: r.subscribers?.source,
                              profile_score: r.subscribers?.profile_score,
                              profile_score_tier: r.subscribers?.profile_score_tier,
                              custom_data: r.subscribers?.custom_data,
                            }}
                            className="block min-w-0 flex-1 max-w-[10rem] sm:max-w-[220px]"
                          >
                            <Link
                              to={`/subscribers/${r.subscriber_id}`}
                              className="block hover:text-emerald-600 dark:hover:text-emerald-400"
                              title="Hover for profile · click to open"
                            >
                              <div className="font-medium truncate text-emerald-600 dark:text-emerald-400">
                                {r.subscribers?.email ?? '—'}
                              </div>
                              {r.subscribers?.name ? (
                                <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {r.subscribers.name}
                                </div>
                              ) : null}
                            </Link>
                          </SubscriberHoverCard>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      <div
                        className="sm:hidden text-xs text-gray-500 dark:text-gray-400 truncate max-w-[10rem]"
                        title={stepLabel(r)}
                      >
                        {stepLabel(r)}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPreviewRow(r)}
                        className="sm:hidden mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
                      >
                        <Eye className="w-3 h-3" />
                        Preview
                      </button>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-gray-600 dark:text-gray-300">
                      <div className="truncate max-w-[180px]" title={stepLabel(r)}>
                        {stepLabel(r)}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-4 py-2 sm:py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      <ScheduledWhen value={r.scheduled_at} />
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      {formatTime(r.sent_at)}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPreviewRow(r)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                          title="Preview what this subscriber will receive"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Preview
                        </button>
                        {r.status === 'pending' ? (
                          <button
                            type="button"
                            onClick={() => setSendNowRow(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                          >
                            <SendHorizonal className="w-3.5 h-3.5" />
                            Send now
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows.length > 0 ? (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div>
            Page {page + 1}
            {listTotal > 0 ? ` · ${listTotal.toLocaleString()} matching` : null}
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={rows.length < PAGE_SIZE}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
